import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./MantenimientoUnidadesModal.css";

import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  getDoc,
} from "firebase/firestore";
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import app from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";

const db = getFirestore(app);
const auth = getAuth(app);

function norm(str) {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "");
}

function isInspectorRole(rol) {
  const r = norm(rol);
  return (
    r === "inspectormaterialmayor" ||
    r === "inspector_material_mayor" ||
    r === "inspectormayor"
  );
}

function formatDateChile(ts) {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
  if (!d || isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CL", { timeZone: "America/Santiago" });
}

function ymdFromTs(ts) {
  if (!ts) return "";
  const d = ts?.toDate ? ts.toDate() : null;
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function tsFromYmd(ymd) {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

function nowChileDate() {
  const nowLocal = new Date();
  return new Date(
    nowLocal.toLocaleString("en-US", { timeZone: "America/Santiago" })
  );
}

// diferencia por día (medianoche)
function daysUntil(ts) {
  if (!ts) return null;
  const d = ts?.toDate ? ts.toDate() : null;
  if (!d || isNaN(d.getTime())) return null;

  const now = nowChileDate();
  const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startD = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = startD.getTime() - startNow.getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

function fieldStatus(ts) {
  const du = daysUntil(ts);
  if (du === null) return { level: "none", du: null };
  if (du < 0) return { level: "expired", du };
  if (du <= 30) return { level: "due", du };
  return { level: "ok", du };
}

function statusClass(ts) {
  const s = fieldStatus(ts);
  if (s.level === "expired") return "mu-badge mu-badge-red";
  if (s.level === "due") return "mu-badge mu-badge-yellow";
  if (s.level === "ok") return "mu-badge";
  return "mu-badge mu-badge-empty";
}

function alertClass(ts) {
  const s = fieldStatus(ts);
  if (s.level === "expired") return "mu-alert mu-alert-red";
  if (s.level === "due") return "mu-alert mu-alert-yellow";
  if (s.level === "ok") return "mu-alert mu-alert-ok";
  return "mu-alert mu-alert-empty";
}

function alertText(ts) {
  const s = fieldStatus(ts);
  if (s.level === "expired") {
    const daysAgo = Math.abs(s.du);
    return `⚠️ VENCIDO hace ${daysAgo} día${daysAgo === 1 ? "" : "s"}`;
  }
  if (s.level === "due") {
    return `⏳ Vence en ${s.du} día${s.du === 1 ? "" : "s"}`;
  }
  if (s.level === "ok") return "✅ Vigente";
  return "—";
}

function safeUpper(str) {
  return String(str || "").trim().toUpperCase();
}

function unitCardOverall(u) {
  const fields = [u.permisoVence, u.soapVence, u.revTecVence];
  let hasExpired = false;
  let hasDue = false;

  for (const f of fields) {
    const s = fieldStatus(f);
    if (s.level === "expired") hasExpired = true;
    else if (s.level === "due") hasDue = true;
  }

  if (hasExpired) return { level: "expired", text: "⚠️ Tiene documentos vencidos" };
  if (hasDue) return { level: "due", text: "⏳ Tiene documentos por vencer" };
  return { level: "ok", text: "✅ Sin alertas" };
}

export default function MantenimientoUnidadesModal({
  open,
  onClose,
  companiaActual,
}) {
  const { cuerpoId } = useCuerpo();
  const [unidades, setUnidades] = useState([]);

  // login / permisos
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState(null); // "create" | "edit"
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [authError, setAuthError] = useState("");
  const [checking, setChecking] = useState(false);

  // editor visible (después de login OK)
  const [showEditor, setShowEditor] = useState(false);

  // form
  const [editingId, setEditingId] = useState(null);
  const [formUnidad, setFormUnidad] = useState("");
  const [permisoVence, setPermisoVence] = useState("");
  const [soapVence, setSoapVence] = useState("");
  const [revTecVence, setRevTecVence] = useState("");
  const [kmUltMant, setKmUltMant] = useState("");
  const [kmProxMant, setKmProxMant] = useState("");
  const [saving, setSaving] = useState(false);

  const ciaNombre = companiaActual?.nombre || "";
  const ciaSlug = companiaActual?.slug || "";
  const canQuery = Boolean(ciaNombre && ciaSlug);

  // Resumen alertas
  const resumen = useMemo(() => {
    let vencidas = 0;
    let porVencer = 0;

    unidades.forEach((u) => {
      [u.permisoVence, u.soapVence, u.revTecVence].forEach((f) => {
        const s = fieldStatus(f);
        if (s.level === "expired") vencidas += 1;
        else if (s.level === "due") porVencer += 1;
      });
    });

    return { vencidas, porVencer };
  }, [unidades]);

  useEffect(() => {
    if (!open) return;

    // ✅ Al abrir modal: aseguramos sesión limpia y reseteamos UI
    (async () => {
      try {
        await signOut(auth);
      } catch (e) {}
    })();

    setUnidades([]);
    setShowLogin(false);
    setShowEditor(false);
    setLoginMode(null);
    setEmail("");
    setPass("");
    setAuthError("");
    setChecking(false);

    setEditingId(null);
    setFormUnidad("");
    setPermisoVence("");
    setSoapVence("");
    setRevTecVence("");
    setKmUltMant("");
    setKmProxMant("");
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open || !canQuery) return;

    const q = query(collection(db, "unidades_emergencia"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => (u.cuerpo_id || "Huasco") === cuerpoId && u.companiaSlug === ciaSlug);
        list.sort((a, b) =>
          String(a.unidad || "").localeCompare(String(b.unidad || ""))
        );
        setUnidades(list);
      },
      (err) => {
        console.error("Error onSnapshot unidades_emergencia:", err);
      }
    );

    return () => unsub();
  }, [open, canQuery, ciaSlug, cuerpoId]);

  const overlayRoot = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.body;
  }, []);

  async function hardClose() {
    // ✅ SOLO AQUÍ se cierra sesión
    try {
      await signOut(auth);
    } catch (e) {}
    if (typeof onClose === "function") onClose();
  }

  function openCreate() {
    setAuthError("");
    setLoginMode("create");
    setShowLogin(true);
    setShowEditor(false);

    setEditingId(null);
    setFormUnidad("");
    setPermisoVence("");
    setSoapVence("");
    setRevTecVence("");
    setKmUltMant("");
    setKmProxMant("");
  }

  function openEdit(u) {
    setAuthError("");
    setLoginMode("edit");
    setShowLogin(true);
    setShowEditor(false);

    setEditingId(u.id);
    setFormUnidad(u.unidad || "");
    setPermisoVence(ymdFromTs(u.permisoVence));
    setSoapVence(ymdFromTs(u.soapVence));
    setRevTecVence(ymdFromTs(u.revTecVence));
    setKmUltMant(u.kmUltMant ?? "");
    setKmProxMant(u.kmProxMant ?? "");
  }

  async function validateInspectorForThisCompany(user) {
    const ref = doc(db, "administradores", user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      return { ok: false, msg: "Este usuario no existe en administradores." };
    }
    const data = snap.data() || {};
    const rol = data.rol || "";
    const compania = data.compania || data.compañía || "";
    const cuerpoAdmin = data.cuerpo_id || "Huasco";

    if (!isInspectorRole(rol)) {
      return { ok: false, msg: "No tienes el rol Inspector Material Mayor." };
    }
    if (!cuerpoId || cuerpoAdmin !== cuerpoId) {
      return { ok: false, msg: "Tu usuario pertenece a otro Cuerpo de Bomberos." };
    }
    if (!compania || !ciaNombre) {
      return { ok: false, msg: "No se pudo validar compañía del usuario." };
    }
    if (norm(compania) !== norm(ciaNombre)) {
      return { ok: false, msg: "Tu Usuario no coincide con esta Compañía." };
    }
    return { ok: true, msg: "" };
  }

  async function doLoginAndAuthorize(e) {
    e?.preventDefault?.();
    setAuthError("");
    setChecking(true);

    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
      const user = cred.user;

      const v = await validateInspectorForThisCompany(user);
      if (!v.ok) {
        setAuthError(v.msg);
        try {
          await signOut(auth);
        } catch (e) {}
        setChecking(false);
        return;
      }

      // ✅ autorizado: cerramos login y mostramos editor
      setShowLogin(false);
      setShowEditor(true);
      setChecking(false);
    } catch (err) {
      console.error("Login error:", err);
      setAuthError("Credenciales inválidas o usuario no autorizado.");
      try {
        await signOut(auth);
      } catch (e) {}
      setChecking(false);
    }
  }

  async function cancelAuthFlow() {
    // cancelar login => cerrar sesión (porque no queremos dejar login abierto si no continúa)
    setAuthError("");
    setShowLogin(false);
    setShowEditor(false);
    setLoginMode(null);
    try {
      await signOut(auth);
    } catch (e) {}
  }

  function cancelEditorOnly() {
    // ✅ cancelar editor NO cierra sesión (como pediste)
    setAuthError("");
    setShowEditor(false);
    setLoginMode(null);
    setEditingId(null);
    setFormUnidad("");
    setPermisoVence("");
    setSoapVence("");
    setRevTecVence("");
    setKmUltMant("");
    setKmProxMant("");
  }

  async function saveUnidad() {
    if (!auth.currentUser) {
      setAuthError("Debes iniciar sesión.");
      setShowLogin(true);
      setShowEditor(false);
      return;
    }

    setSaving(true);
    setAuthError("");

    try {
      // revalidar por seguridad
      const v = await validateInspectorForThisCompany(auth.currentUser);
      if (!v.ok) {
        setAuthError(v.msg);
        try {
          await signOut(auth);
        } catch (e) {}
        setSaving(false);
        return;
      }

      const unidadUp = safeUpper(formUnidad);
      if (!unidadUp) {
        setAuthError("Debes ingresar el nombre/código de la unidad (ej: B3).");
        setSaving(false);
        return;
      }

      const docId = editingId || `${ciaSlug}_${unidadUp}`;
      const ref = doc(db, "unidades_emergencia", docId);

      const payload = {
        cuerpo_id: cuerpoId,
        companiaSlug: ciaSlug,
        companiaNombre: ciaNombre,
        unidad: unidadUp,
        permisoVence: tsFromYmd(permisoVence),
        soapVence: tsFromYmd(soapVence),
        revTecVence: tsFromYmd(revTecVence),
        kmUltMant: kmUltMant === "" ? null : Number(kmUltMant),
        kmProxMant: kmProxMant === "" ? null : Number(kmProxMant),
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser.uid,
      };

      if (editingId) {
        await updateDoc(ref, payload);
      } else {
        await setDoc(ref, payload);
      }

      // ✅ NO cerrar sesión aquí
      // Solo limpiamos/cerramos editor (la sesión se mantiene hasta cerrar el modal)
      cancelEditorOnly();
    } catch (err) {
      console.error("Error guardando unidad:", err);
      setAuthError("Error al guardar. Revisa consola.");
    } finally {
      setSaving(false);
    }
  }

  if (!open || !overlayRoot) return null;

  return createPortal(
    <div className="mu-overlay" role="dialog" aria-modal="true">
      <div className="mu-modal">
        <div className="mu-header">
          <div>
            <div className="mu-title">Mantención de Unidades</div>
            <div className="mu-subtitle">
              {ciaNombre ? `Compañía: ${ciaNombre}` : "Compañía no detectada en URL"}
            </div>
          </div>
          <button className="mu-close" onClick={hardClose} title="Cerrar">
            ✕
          </button>
        </div>

        <div className="mu-actions">
          <button className="mu-btn" onClick={openCreate} disabled={!canQuery}>
            ➕ Crear unidad
          </button>
          <div className="mu-hint">
            * Ver: público. Crear/Editar: requiere login Inspector Material Mayor.
          </div>
        </div>

        {!canQuery && (
          <div className="mu-error">
            No se pudo detectar la compañía desde la URL. (Debe ser /cia/&lt;slug&gt;)
          </div>
        )}

        {/* ✅ Banner resumen alertas */}
        {canQuery && (resumen.vencidas > 0 || resumen.porVencer > 0) && (
          <div className="mu-summary">
            <div className="mu-summary-item mu-summary-red">
              ⚠️ Vencidas: <b>{resumen.vencidas}</b>
            </div>
            <div className="mu-summary-item mu-summary-yellow">
              ⏳ Por vencer: <b>{resumen.porVencer}</b>
            </div>
          </div>
        )}

        {/* LISTADO PUBLICO */}
        <div className="mu-list">
          {unidades.length === 0 ? (
            <div className="mu-empty">No hay unidades registradas aún.</div>
          ) : (
            unidades.map((u) => {
              const overall = unitCardOverall(u);
              const overallClass =
                overall.level === "expired"
                  ? "mu-card-alert mu-card-alert-red"
                  : overall.level === "due"
                  ? "mu-card-alert mu-card-alert-yellow"
                  : "mu-card-alert mu-card-alert-ok";

              return (
                <div key={u.id} className="mu-card">
                  <div className={`mu-card-alert ${overallClass}`}>
                    {overall.text}
                  </div>

                  <div className="mu-card-top">
                    <div className="mu-unit">{u.unidad || "SIN NOMBRE"}</div>

                    <button
                      className="mu-btn mu-btn-outline"
                      onClick={() => openEdit(u)}
                      title="Editar (requiere login)"
                    >
                      ✏️ Editar
                    </button>
                  </div>

                  <div className="mu-grid">
                    <div className="mu-row">
                      <span className="mu-label">Permiso de Circulación</span>
                      <div className="mu-right">
                        <span className={statusClass(u.permisoVence)}>
                          {formatDateChile(u.permisoVence)}
                        </span>
                        <span className={alertClass(u.permisoVence)}>
                          {alertText(u.permisoVence)}
                        </span>
                      </div>
                    </div>

                    <div className="mu-row">
                      <span className="mu-label">SOAP</span>
                      <div className="mu-right">
                        <span className={statusClass(u.soapVence)}>
                          {formatDateChile(u.soapVence)}
                        </span>
                        <span className={alertClass(u.soapVence)}>
                          {alertText(u.soapVence)}
                        </span>
                      </div>
                    </div>

                    <div className="mu-row">
                      <span className="mu-label">Revisión Técnica</span>
                      <div className="mu-right">
                        <span className={statusClass(u.revTecVence)}>
                          {formatDateChile(u.revTecVence)}
                        </span>
                        <span className={alertClass(u.revTecVence)}>
                          {alertText(u.revTecVence)}
                        </span>
                      </div>
                    </div>

                    <div className="mu-row">
                      <span className="mu-label">KM Última Mantención</span>
                      <span className="mu-value">{u.kmUltMant ?? "—"}</span>
                    </div>

                    <div className="mu-row">
                      <span className="mu-label">KM Próxima Mantención</span>
                      <span className="mu-value">{u.kmProxMant ?? "—"}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* LOGIN */}
        {showLogin && (
          <div className="mu-login">
            <div className="mu-login-box">
              <div className="mu-login-title">
                {loginMode === "create" ? "Crear unidad" : "Editar unidad"} (Login requerido)
              </div>

              <form onSubmit={doLoginAndAuthorize} className="mu-login-form">
                <input
                  className="mu-input"
                  type="email"
                  placeholder="Correo"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
                <input
                  className="mu-input"
                  type="password"
                  placeholder="Contraseña"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  autoComplete="current-password"
                  required
                />

                {authError ? <div className="mu-error">{authError}</div> : null}

                <div className="mu-login-actions">
                  <button className="mu-btn" type="submit" disabled={checking}>
                    {checking ? "Validando..." : "Ingresar"}
                  </button>
                  <button
                    className="mu-btn mu-btn-outline"
                    type="button"
                    onClick={cancelAuthFlow}
                  >
                    Cancelar
                  </button>
                </div>
              </form>

              <div className="mu-hint" style={{ marginTop: 10 }}>
                * Por seguridad, la sesión del inspector se cierra al cerrar esta ventana.
              </div>
            </div>
          </div>
        )}

        {/* EDITOR (solo si login OK) */}
        {showEditor && auth.currentUser && (loginMode === "create" || loginMode === "edit") && (
          <div className="mu-editor">
            <div className="mu-editor-title">
              {editingId ? "Editar unidad" : "Crear unidad"}
            </div>

            {authError ? <div className="mu-error">{authError}</div> : null}

            <div className="mu-editor-grid">
              <div className="mu-field">
                <label>Unidad (ej: B3, BH3, RX3)</label>
                <input
                  className="mu-input"
                  value={formUnidad}
                  onChange={(e) => setFormUnidad(e.target.value)}
                  placeholder="B3"
                />
              </div>

              <div className="mu-field">
                <label>Permiso de Circulación (vence)</label>
                <input
                  className="mu-input"
                  type="date"
                  value={permisoVence}
                  onChange={(e) => setPermisoVence(e.target.value)}
                />
              </div>

              <div className="mu-field">
                <label>SOAP (vence)</label>
                <input
                  className="mu-input"
                  type="date"
                  value={soapVence}
                  onChange={(e) => setSoapVence(e.target.value)}
                />
              </div>

              <div className="mu-field">
                <label>Revisión Técnica (vence)</label>
                <input
                  className="mu-input"
                  type="date"
                  value={revTecVence}
                  onChange={(e) => setRevTecVence(e.target.value)}
                />
              </div>

              <div className="mu-field">
                <label>KM Última Mantención</label>
                <input
                  className="mu-input"
                  type="number"
                  value={kmUltMant}
                  onChange={(e) => setKmUltMant(e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="mu-field">
                <label>KM Próxima Mantención</label>
                <input
                  className="mu-input"
                  type="number"
                  value={kmProxMant}
                  onChange={(e) => setKmProxMant(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="mu-editor-actions">
              <button className="mu-btn" onClick={saveUnidad} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
              <button className="mu-btn mu-btn-outline" onClick={cancelEditorOnly}>
                Cancelar
              </button>
            </div>

            <div className="mu-hint" style={{ marginTop: 10 }}>
              * La sesión del inspector se cierra al cerrar el modal (X o botón Cerrar).
            </div>
          </div>
        )}

        <div className="mu-footer">
          <button className="mu-btn mu-btn-outline" onClick={hardClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    overlayRoot
  );
}
